import {
  MAX_VALUE_CONSTRAINTS,
  MIN_VALUE,
  FORM_VALIDATION_MSG,
  MIN_LENGTH_CONSTRAINTS,
  PICKUP_MANAGEMENT_MODES,
} from "../constants";
import {ALPHA_NUMERIC_REGEX} from "../../../constRegex";
import {
  fetchLocationMappingData,
  isBulkEditPickupMode,
  isEditOrMergePickupMode,
  isReadOnlyPickupViewMode,
} from "../utils";
import { v4 as uuidv4 } from 'uuid';

const validateHU = (value) => {
  if (value > MAX_VALUE_CONSTRAINTS.CREATE_PICKUP_FORM.HU) {
    return FORM_VALIDATION_MSG.HU.INVALID_MAX_VALUE;
  }
  return null;
};


const validateWeight = (value) => {
  if (!value) {
    return FORM_VALIDATION_MSG.WEIGHT.REQUIRED;
  } else if (value > MAX_VALUE_CONSTRAINTS.CREATE_PICKUP_FORM.WEIGHT) {
    return FORM_VALIDATION_MSG.WEIGHT.INVALID_MAX_VALUE;
  } else if (value < MIN_VALUE.CREATE_PICKUP_FORM.WEIGHT) {
    return FORM_VALIDATION_MSG.WEIGHT.INVALID_MIN_VALUE;
  }
  return null;
};

const validateZip = (value) => {
  if (!value) {
    return FORM_VALIDATION_MSG.ZIP_CODE.REQUIRED;
  } else if (!ALPHA_NUMERIC_REGEX.test(value)) {
    return FORM_VALIDATION_MSG.ZIP_CODE.INVALID_VALUE;
  } else if (value > MAX_VALUE_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_ZIP) {
    return FORM_VALIDATION_MSG.ZIP_CODE.INVALID_MAX_VALUE;
  }
  return null;
};

const validatePackageCode = (value) => {
  if (!value) {
    return FORM_VALIDATION_MSG.PACKAGE_CODE.REQUIRED;
  }
  return null;
};

const getDefaultShipment = (shipment) => {
  return {
    pickupShipmentId: shipment?.pickupShipmentId ?? null,
    pickupNumber: shipment?.pickupNumber ?? "",
    pieces: shipment?.pieces ?? "",
    packageCode: shipment?.packageCode ?? null,
    weight: shipment?.weight ?? "",
    handlingUnits: shipment?.hu ?? "",
    destinationZip: shipment?.consigneeZip ?? null,
    priority: shipment?.priority ?? null,
    destinationCity: shipment?.consigneeCity ?? null,
    destinationState: shipment?.consigneeState ?? null,
    consigneeCode: shipment?.consigneeCode ?? null,
    destinationTerminal: shipment?.destinationTerminal ?? null,
    sequenceNumber : shipment?.seq ?? null,
    pickupShipmentRowId: uuidv4(),
    isFreezable: shipment?.isFreezable ?? null,
    ...(shipment?.pickupId ? { pickupId: shipment.pickupId } : {}),
  };
};

const getFieldError = ({ field, value, mode , isZipUpdatedManually }) => {
  const isReadOnly = isReadOnlyPickupViewMode(mode);
  const shouldSkipValidation = (isEditOrMergePickupMode(mode) || isBulkEditPickupMode(mode)) && !value;
  if (isReadOnly || shouldSkipValidation) {
    return null;
  }

  switch (field) {
    case "handlingUnits":
      return validateHU(value);
    case "weight":
      return validateWeight(value);
    case "destinationZip": //skip zipcode validation for create pickup if value is not provided
      return (PICKUP_MANAGEMENT_MODES.CREATE_PICKUP === mode && !value  || !isZipUpdatedManually ) ? null: validateZip(value) ;
    case "packageCode":
    case "packageCodeWithSuffix":
      return validatePackageCode(value);
    default:
      return null;
  }
};

const getDefaultShipmentError = () => {
  return {
    pieces: null,
    weight: null,
    handlingUnits: null,
    destinationZip: null,
    packageCode: null,
  };
};

const isEmptyShipmentObj = (shipment) => {
  const {
    pickupShipmentRowId,
    isZipUpdatedManually,
    isFreezable,
    ...shipmentData
  } = shipment;
  return Object.values(shipmentData).filter(Boolean).length === 0;
};

const isNonEmptyShipmentObj = (shipment) => {
  return !isEmptyShipmentObj(shipment);
};

const getShipmentErrorInfo = (shipments, mode) => {
  const shipmentsErrors = [];
  shipments.forEach((eachRow, rowIndex) => {
    if (isEmptyShipmentObj(eachRow)) {
      return;
    }
    Object.keys(getDefaultShipment()).forEach((fieldName) => {
      shipmentsErrors[rowIndex] = {
        ...shipmentsErrors[rowIndex],
        [fieldName]: getFieldError({
          field: fieldName,
          value: shipments[rowIndex][fieldName],
          mode,
          isZipUpdatedManually: shipments[rowIndex].isZipUpdatedManually
        }),
      };
    });
  });
  return shipmentsErrors;
};

const checkAtLeastOneShipmentInvalid = (shipmentErrorInfo) => {
  return Object.values(shipmentErrorInfo).filter((value) => value).length;
};

const getShipmentsParameterCount = (shipments) => {
  const lastRowIndex = shipments.length - 1;
  return shipments.reduce(
    (countObj, shipmentObj, index) => {
      if (Boolean(shipmentObj.pieces)) {
        countObj.totalPieces += shipmentObj.pieces;
      }
      if (Boolean(shipmentObj.weight)) {
        countObj.totalWeight += shipmentObj.weight;
      }
      if (lastRowIndex === index) {
        if (isEmptyShipmentObj(shipmentObj));
        else {
          countObj.totalShipments += 1;
        }
      } else {
        countObj.totalShipments += 1;
      }
      return countObj;
    },
    { totalPieces: 0, totalWeight: 0, totalShipments: 0 }
  );
};

const fetchLocationData = ({
  inputValue: zipCode,
  terminalCode,
  getOptions,
}) => {
  if (
    !zipCode ||
    zipCode.length < MIN_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.DESTINATION_ZIP
  ) {
    return Promise.resolve([]);
  }
  return fetchLocationMappingData({
    terminalCode,
    zipCode,
  }).then(
    (response) => {
      if (response && response.status === 200) {
        getOptions(response.data);
        return response.data;
      } else {
        getOptions([], true);
        return [];
      }
    },
    (_error) => {
      getOptions([]);
      return [];
    }
  );
};

export {
  getFieldError,
  getShipmentErrorInfo,
  getDefaultShipmentError,
  getDefaultShipment,
  checkAtLeastOneShipmentInvalid,
  getShipmentsParameterCount,
  isEmptyShipmentObj,
  isNonEmptyShipmentObj,
  fetchLocationData,
};
